import { useEffect, useState } from "react";
import { X, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/crm";
import { useSettings } from "@/contexts/SettingsContext";

const inputCls = "w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-sm outline-none focus:border-neutral-900 dark:focus:border-white transition";

const empty = {
  full_name: "",
  email: "",
  phone: "",
  nationality: "",
  service: "",
  status: "",
  source: "manual",
  assigned_to: "",
  message: "",
  proposal_value: "",
  expected_revenue: "",
};

export default function NewLeadDialog({ open, onClose, onCreated, defaultStatus }) {
  const { settings, refresh } = useSettings();
  const stages = settings.stages || [];
  const services = settings.services || [];
  const sources = settings.sources || [];
  const [data, setData] = useState({ ...empty });
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Self-heal: if settings failed to load earlier, retry now.
      if (!services.length || !stages.length) refresh?.();
      setData({
        ...empty,
        service: services[0]?.label || "",
        status: defaultStatus || stages[0]?.label || "",
      });
      crmApi.listUsers().then(setUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultStatus, services.length, stages.length]);

  if (!open) return null;

  const setField = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!data.full_name.trim() || !data.service.trim()) {
      toast.error("Name and service are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: data.full_name.trim(),
        email: data.email.trim() || null,
        phone: data.phone.trim() || null,
        nationality: data.nationality.trim() || null,
        service: data.service,
        status: data.status,
        source: data.source.trim() || "manual",
        assigned_to: data.assigned_to || null,
        message: data.message,
        proposal_value: data.proposal_value === "" ? null : parseFloat(data.proposal_value),
        expected_revenue: data.expected_revenue === "" ? null : parseFloat(data.expected_revenue),
      };
      const created = await crmApi.createLead(payload);
      toast.success("Lead created");
      onCreated?.(created);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to create lead";
      toast.error(typeof msg === "string" ? msg : "Failed to create lead");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="new-lead-dialog">
      <div className="bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Add manually</div>
            <div className="mt-1 font-serif text-2xl">New Lead</div>
          </div>
          <button onClick={onClose} data-testid="new-lead-close" className="text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition"><X size={18}/></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Full name *</div>
              <input data-testid="new-lead-fullname" className={inputCls} value={data.full_name} onChange={setField("full_name")} required />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Email</div>
              <input data-testid="new-lead-email" className={inputCls} type="email" value={data.email} onChange={setField("email")} />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Phone</div>
              <input data-testid="new-lead-phone" className={inputCls} value={data.phone} onChange={setField("phone")} />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Nationality</div>
              <input data-testid="new-lead-nationality" className={inputCls} value={data.nationality} onChange={setField("nationality")} />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Service *</div>
              <select data-testid="new-lead-service" className={inputCls} value={data.service} onChange={setField("service")} required>
                {services.map((s) => <option key={s.id}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Stage</div>
              <select data-testid="new-lead-status" className={inputCls} value={data.status} onChange={setField("status")}>
                {stages.map((s) => <option key={s.id}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Source</div>
              <input data-testid="new-lead-source" list="lead-sources" className={inputCls} value={data.source} onChange={setField("source")} placeholder="e.g. referral, instagram, event" />
              <datalist id="lead-sources">
                {sources.map((s) => <option key={s.id} value={s.label} />)}
              </datalist>
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Assigned to</div>
              <select data-testid="new-lead-assigned" className={inputCls} value={data.assigned_to} onChange={setField("assigned_to")}>
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Proposal value (AED)</div>
              <input data-testid="new-lead-proposal" type="number" min="0" className={inputCls} value={data.proposal_value} onChange={setField("proposal_value")} />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Expected revenue (AED)</div>
              <input data-testid="new-lead-expected" type="number" min="0" className={inputCls} value={data.expected_revenue} onChange={setField("expected_revenue")} />
            </label>
          </div>

          <label className="block">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">Notes / initial message</div>
            <textarea data-testid="new-lead-message" className={inputCls + " min-h-[90px]"} value={data.message} onChange={setField("message")} />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} data-testid="new-lead-cancel" className="px-5 py-2 border border-neutral-200 dark:border-neutral-800 text-[11px] tracking-overline uppercase hover:bg-neutral-100 dark:hover:bg-neutral-900">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="new-lead-submit" className="inline-flex items-center gap-2 px-5 py-2 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase disabled:opacity-60">
              {submitting ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Create Lead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
